<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div class="card shadow mb-3">

    <!-- Delete Resource Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h2>Delete Resource</h2>
            <button type="button btn-lg" class="close" data-dismiss="modal" aria-label="Cancel">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p v-if="existingData.is_template" class="lead">If you proceed, all data about the template will be deleted. This action is irreversible.</p>
            <p v-else class="lead">If you proceed, the resource will be unpublished from any registry in which it is published and all data about the resource will be deleted.</p>
            <div class="d-flex gap-2 pt-2" style="gap: 10px;">
              <button type="button" class="btn btn-sm btn-danger" data-dismiss="modal">Cancel</button>
              <form method="POST" :action="baseUrls.destroyUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <input type="hidden" name="_method" value="DELETE">
                <button type="submit" class="btn btn-sm btn-registry pull-left">Delete</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer Resource Modal -->
    <div class="modal fade" id="transferModal" tabindex="-1" role="dialog" aria-labelledby="transferModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

            <div class="modal-body" style="padding: 0 30px 30px 30px;">
              <h4 style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Transfer {{ typeOfResource.charAt(0).toUpperCase() + typeOfResource.slice(1) }} Ownership</h4>
              <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">By clicking "Request Transfer", your ownership of this resource will be terminated immediately and transferred to the new resource owner. The new owner must have an existing IoTPI account.</p>

              <form id="transferForm" method="POST" enctype="multipart/form-data" :action="baseUrls.transferUrl">
                <input type="hidden" name="_token" :value="csrf_token">
                <input type="hidden" name="resource" :value="existingData.id">

                <div class="form-group" style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                  <label for="new_owner_email" style="font-weight: bold; font-size: 16px; color: #333; white-space: nowrap; padding-top: 8px; flex-shrink: 0;">New Owner Email</label>
                  <div style="flex-grow: 1;">
                    <input type="email" id="new_owner_email" name="new_owner_email" placeholder="" required="required" class="form-control" @input="validateEmail" style="margin-bottom: 8px;">
                    <small id="new_owner_email-help" class="form-text text-muted">Note: The email address of the new owner must be what they use for their IoTPI account</small>
                  </div>
                </div>

                <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                  <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
                  <button type="submit" id="transfer-form-button" class="btn btn-registry" :disabled="!isEmailValid" :style="{opacity: isEmailValid ? 1 : 0.6, cursor: isEmailValid ? 'pointer' : 'not-allowed'}">Request Transfer</button>
                </div>
              </form>

            </div>

        </div>
      </div>
    </div>

    <div class="card-body">
      <div class="card-text">
        <center>
          <h1>{{ existingData.name }}</h1>
          <p v-if="!existingData.is_template"><small>Resource ID # {{ existingData.id }}</small></p>
          <p v-else><small>Template ID # {{ existingData.id }}</small></p>
          <p class="lead text-muted">{{ existingData.description }}<br>
          <div v-if="existingData.image"><img :src="existingData.image"></div>
          <div class="img-thumb" v-else>Image Not Provided</div></p>
          <!-- <h5>CONTRIBUTOR:&nbsp;<a :href="baseUrls.userBaseUrl + existingData.user_id">{{ existingData.contributor }}</a></h5> -->
          <h6>CONTRIBUTOR:&nbsp; {{ existingData.contributor }} </h6>

          <button v-if="(existingData.isOwner || permissions.currentPermissions.SYS_DELETE_RESOURCE )" type="button" class="btn btn-danger" data-toggle="modal" data-target="#deleteModal">Delete</button>
          <a :href="baseUrls.editUrl" v-if="existingData.isOwner" type="button" class="btn btn-registry">Edit</a>
          <button v-if="(existingData.isOwner || permissions.currentPermissions.SYS_TRANSFER_RESOURCE )" type="button" class="btn btn-registry" data-toggle="modal" data-target="#transferModal">Transfer Ownership</button>
          <a :href="baseUrls.publishUrl" v-if="(existingData.isOwner || permissions.currentPermissions.SYS_DELETE_RESOURCE) && !existingData.is_template && existingData.isComplete" type="button" class="btn btn-registry">Publish</a>
          <a :href="baseUrls.loadTemplateUrl" v-if="existingData.isOwner && existingData.is_template" type="button" class="btn btn-success">Create Resource</a>
        </center>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        existingData: [Object, Array],
        isTemplate: [Boolean, Number],
        permissions: Object,
        csrf_token: String,
      },
      data: function() {
        return {
          isEmailValid: false,
          baseUrls: {
            resourceBaseUrl: '/resource/',
            irrBaseUrl: '/irr/',
            userBaseUrl: '/user/',
            destroyUrl: '/resource/' + this.existingData.id + '/destroy',
            editUrl: '/resource/' + this.existingData.id + '/edit/',
            publishUrl: '/resource/' + this.existingData.id + '/publish/',
            transferUrl: '/resource/transfer',
            loadTemplateUrl: '/template/' + this.existingData.id + '/load/',
          },
        }
      },
      methods: {
        validateEmail() {
          const email = document.getElementById('new_owner_email').value;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          this.isEmailValid = emailRegex.test(email);
        }
      },
      computed: {
        typeOfResource: function () {
          return (this.isTemplate) ? 'template' : 'resource';
        }
      },
      mounted() {
        const urlParams = new URLSearchParams(window.location.search);

        //check for 'delete' in query string, display delete modal if applicable
        if(this.permissions.currentPermissions.SYS_DELETE_RESOURCE || this.existingData.isOwner) {
          if(urlParams.get('delete') == 1) {
            $('#deleteModal').modal('show');
          }
        }

        //check for 'transfer' in query string, display transfer modal if applicable
        if(this.permissions.currentPermissions.SYS_TRANSFER_RESOURCE || this.existingData.isOwner) {
          if(urlParams.get('transfer') == 1) {
            $('#transferModal').modal('show');
          }
        } else {
          $('#transferModal').html('');
        }
      },
    }
</script>
<style>
.img-thumb {
  width: 200px;
  height: 200px;
  border: 1px solid;
}
</style>
